import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import {
  GridColDef,
  GridFilterModel,
  GridRowId,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetOrionTransactionsQuery } from "features/trading/accounts";
import { gridModelsToQuery } from "features/api";
import type {
  OrionTransaction,
  OrionTransactionParams,
  PDFReportSettingsInfo,
  ReportType,
} from "helpers/types";
import {
  ServerDataGrid,
  currencyFormatter,
  dateFormatter,
  qtyFormatter,
  useGridFilters,
  usePagination,
  useSorting,
} from "components/ui/ServerDataGrid";
import { transactionModalStyles } from "styles/TransactionModal.styles";

interface TransactionModalProps {
  open: boolean;
  onClose: () => void;
  settingsInfo: PDFReportSettingsInfo;
  reportType: ReportType;
  onDeselectedTransactionsChange?: (
    deselectedTransactions: OrionTransaction[],
  ) => void;
}

const TransactionModal = ({
  open,
  onClose,
  reportType,
  settingsInfo,
  onDeselectedTransactionsChange,
}: TransactionModalProps) => {
  const [start_date, end_date] = settingsInfo.period;
  const strategy_id = settingsInfo.strategy_id ?? undefined;
  const orion_account_id = settingsInfo.orion_account_id ?? undefined;
  const [pagination, setPagination] = usePagination(25, 0);
  const [sorting, setSorting] = useSorting("transaction_date", "desc");
  const [gridFilters, setGridFilters] = useGridFilters();
  const [searchValue, setSearchValue] = useState("");
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<
    GridRowId[]
  >([]);
  const [selectedTransactionsById, setSelectedTransactionsById] = useState<
    Record<number, OrionTransaction>
  >({});

  const canFetch = Boolean(
    start_date && end_date && strategy_id && orion_account_id,
  );

  const params = useMemo(() => {
    const baseParams: OrionTransactionParams = {
      start_date: start_date || "",
      end_date: end_date || "",
      strategy_id: strategy_id || 0,
      orion_account_id: orion_account_id || 0,
      report_type: reportType,
    };

    const queryParams =
      gridModelsToQuery({
        pagination,
        sorting,
        gridFilters,
        filters: baseParams,
      }) || {};

    // Keep `order_by` for compatibility with endpoints expecting this name.
    if (queryParams.ordering && !queryParams.order_by) {
      queryParams.order_by = queryParams.ordering;
    }

    return {
      ...baseParams,
      ...queryParams,
      search: searchValue || undefined,
    };
  }, [
    start_date,
    end_date,
    strategy_id,
    orion_account_id,
    pagination.page,
    pagination.pageSize,
    sorting,
    gridFilters,
    searchValue,
  ]);

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setGridFilters(model);

      const nextSearch = (model.quickFilterValues || [])
        .map((value) => String(value).trim())
        .filter(Boolean)
        .join(" ");

      setSearchValue(nextSearch);
      setPagination((prev) => ({ ...prev, page: 0 }));
    },
    [setGridFilters, setPagination],
  );

  const { data, isLoading, isFetching, error } = useGetOrionTransactionsQuery(
    params,
    { skip: !open || !canFetch },
  );

  useEffect(() => {
    if (!data?.results?.length) return;

    setSelectedTransactionsById((prev) => {
      const next = { ...prev };
      data.results.forEach((transaction) => {
        if (selectedTransactionIds.includes(transaction.id)) {
          next[transaction.id] = transaction;
        }
      });
      return next;
    });
  }, [data?.results, selectedTransactionIds]);

  const deselectedTransactions = useMemo(
    () =>
      selectedTransactionIds
        .map((id) => selectedTransactionsById[Number(id)])
        .filter(Boolean),
    [selectedTransactionIds, selectedTransactionsById],
  );

  useEffect(() => {
    onDeselectedTransactionsChange?.(deselectedTransactions);
  }, [onDeselectedTransactionsChange, deselectedTransactions]);

  const columns: GridColDef<OrionTransaction>[] = [
    {
      field: "transaction_date",
      headerName: "Date",
      minWidth: 140,
      flex: 1,
      renderCell: dateFormatter,
    },
    {
      field: "settlement_date",
      headerName: "Settlement Date",
      minWidth: 140,
      flex: 1,
      renderCell: dateFormatter,
    },
    {
      field: "transaction_type",
      headerName: "Type",
      minWidth: 180,
      flex: 1.2,
    },
    {
      field: "ticker",
      headerName: "Ticker",
      minWidth: 120,
      flex: 0.9,
    },
    {
      field: "transaction_amount",
      headerName: "Amount",
      minWidth: 140,
      flex: 1,
      align: "right",
      headerAlign: "right",
      renderCell: currencyFormatter,
    },
    {
      field: "units_amount",
      headerName: "Units",
      minWidth: 120,
      flex: 0.9,
      align: "right",
      headerAlign: "right",
      renderCell: qtyFormatter,
    },
    {
      field: "nav_price",
      headerName: "Nav Price",
      minWidth: 120,
      flex: 0.9,
      align: "right",
      headerAlign: "right",
      renderCell: currencyFormatter,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 0.9,
    },
    {
      field: "unit_balance",
      headerName: "Unit Balance",
      minWidth: 120,
      flex: 0.9,
      align: "right",
      headerAlign: "right",
      renderCell: qtyFormatter,
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Transactions</DialogTitle>
      <DialogContent>
        {!canFetch ? (
          <Alert severity="info" sx={transactionModalStyles.infoAlert}>
            Select a client account, date range, and strategy before loading
            transactions.
          </Alert>
        ) : error ? (
          <Alert severity="error" sx={transactionModalStyles.errorAlert}>
            Failed to load transactions.
          </Alert>
        ) : (
          <>
            {!isLoading && !isFetching && !data?.results?.length ? (
              <Typography sx={transactionModalStyles.emptyText}>
                No transactions found.
              </Typography>
            ) : null}
            <Typography sx={transactionModalStyles.selectionInfo}>
              Excluded transactions: {deselectedTransactions.length}
            </Typography>
            <ServerDataGrid
              sx={transactionModalStyles.grid}
              rows={data?.results || []}
              rowCount={data?.count || 0}
              loading={isLoading || isFetching}
              columns={columns}
              paginationModel={pagination}
              onPaginationModelChange={setPagination}
              sortModel={sorting}
              onSortModelChange={setSorting}
              filterModel={gridFilters}
              onFilterModelChange={handleFilterModelChange}
              checkboxSelection
              keepNonExistentRowsSelected
              rowSelectionModel={selectedTransactionIds}
              onRowSelectionModelChange={setSelectedTransactionIds}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransactionModal;
